/**
 * Utilidades deterministas compartidas.
 *
 * El tablero no tiene backend: las cifras que no vienen en `data/*.data.ts` se
 * calculan. Para que la demo sea creíble tienen que ser **las mismas en cada
 * carga** —si el NPS de una sucursal cambiara al recargar, se notaría—, así que
 * en vez de `Math.random()` se usa un generador congruencial sembrado con el
 * dato de entrada. Es la misma técnica que ya emplea `results.data.ts` para
 * generar las 135 sucursales que faltaban.
 */

/**
 * Generador congruencial lineal. Mismo `semilla`, misma secuencia, siempre.
 * Las constantes son las de glibc: no hace falta calidad criptográfica, hace
 * falta reproducibilidad.
 */
export const pseudoAleatorio = (semilla: number): (() => number) => {
  let estado = Math.abs(Math.trunc(semilla)) % 2147483648;

  return () => {
    estado = (estado * 1103515245 + 12345) % 2147483648;

    return estado / 2147483648;
  };
};

/** Hash FNV-1a de 32 bits: convierte una cadena en semilla. */
export const hashTexto = (texto: string): number => {
  let hash = 2166136261;

  for (let i = 0; i < texto.length; i++) {
    hash ^= texto.charCodeAt(i);
    hash = Math.imul(hash, 16777619);
  }

  return hash >>> 0;
};

export const limitar = (valor: number, min: number, max: number): number =>
  Math.min(max, Math.max(min, valor));

export const sinAcentos = (texto: string): string =>
  texto.normalize('NFD').replace(/[\u0300-\u036f]/g, '');

/**
 * Forma canónica de un nombre para cruzar tablas.
 *
 * Los archivos de datos escriben la misma sucursal de dos maneras —`SUCURSAL
 * ATIZAPÁN 2` en el ranking y `SUCURSAL ATIZAPAN 2` en el catálogo de
 * filtros—, así que cualquier cruce por nombre tiene que hacerse sin acentos,
 * en mayúsculas y con los espacios colapsados.
 */
export const normalizar = (texto: string): string =>
  sinAcentos(texto).toUpperCase().replace(/\s+/g, ' ').trim();
